import { useState, useEffect } from 'react'
import { Toggle } from '../components/Toggle'
import { TrashIcon } from '../components/icons/TrashIcon'
import { PencilIcon } from '../components/icons/PencilIcon'
import { LightningIcon } from '../components/icons/LightningIcon'
import { WarningIcon } from '../components/icons/WarningIcon'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../lib/api'
import type {
  Skill,
  SkillDependent,
  SkillDependentsResponse,
  SkillRef,
  SkillListResponse,
  SkillCreate,
  SkillUpdate,
  SkillType,
} from '../lib/types'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import { Card } from '../components/Card'
import { Spinner } from '../components/Spinner'
import {
  SkillForm,
  SkillFieldErrors,
  getSkillFormErrors,
  buildSkillFieldErrors,
  focusFirstSkillFormError,
} from '../components/SkillForm'
import { useEntityEditorRegistration } from '../hooks/useEntityEditorRegistration'
import { NotSubscribedCard } from './PreviewPage'

/* ─── TypeBadge ─── */

function TypeBadge({ type }: { type: SkillType }) {
  const { t } = useTranslation()
  if (type === 'Execute') {
    return (
      <span className="badge badge-execute" title={t('skillsTab.badge.executeWarning')}>
        <span aria-hidden="true">⚠</span>{t('skillsTab.editor.typeExecute')}
      </span>
    )
  }
  return <span className="badge badge-read">{t('skillsTab.editor.typeRead')}</span>
}

/* ─── Editor form ─── */

interface EditorForm {
  name: string
  type: SkillType
  shortDescription: string
  agentSummary: string
  definition: string
}

const emptyEditorForm: EditorForm = { name: '', type: 'Read', shortDescription: '', agentSummary: '', definition: '' }

/* ─── Props ─── */

export interface GlobalSkillsTabProps {
  skillBindings: SkillRef[]
  savedBindings: SkillRef[]
  onToggle: (skillId: string, isEnabled: boolean) => void
  onAdded: (skillId: string) => void
  onRemoved: (skillId: string) => void
}

/* ─── Preview card (MS-866 AC1/AC2) ───
   Rendered instead of the full config UI below while FEATURES.globalSkillsConfig
   is false. The legacy UI is retained (hide-not-delete) — see ConnectionsPreview
   in GlobalConnectionsTab.tsx for the precedent. */

export function GlobalSkillsPreview() {
  const { t } = useTranslation()
  return (
    <NotSubscribedCard
      cardTitle={t('globalSkillsTab.preview.cardTitle')}
      description={t('globalSkillsTab.preview.description')}
      voteFeature="Global Skills"
    />
  )
}

/* ─── GlobalSkillsTab ─── */

export function GlobalSkillsTab({ skillBindings, savedBindings, onToggle, onAdded, onRemoved }: GlobalSkillsTabProps) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()

  const [editorMode, setEditorMode] = useState<'idle' | 'create' | 'edit'>('idle')
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null)
  const [editorForm, setEditorForm] = useState<EditorForm>(emptyEditorForm)
  const [originalForm, setOriginalForm] = useState<EditorForm | null>(null)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [pendingEntitySwitch, setPendingEntitySwitch] = useState<Skill | 'create' | null>(null)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<SkillFieldErrors>({})
  const [deleteConfirm, setDeleteConfirm] = useState<Skill | null>(null)
  const [deleteConfirmDependents, setDeleteConfirmDependents] = useState<SkillDependent[]>([])
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const isEditorDirty = originalForm !== null && JSON.stringify(editorForm) !== JSON.stringify(originalForm)

  // Entity-editor registry (Skill/Global) — reuses the existing isEditorDirty
  // snapshot above.
  useEntityEditorRegistration({
    isOpen: () => editorMode !== 'idle',
    isDirty: () => isEditorDirty,
    markClean: () => closeEditor(),
  })

  const [disableConfirm, setDisableConfirm] = useState<Skill | null>(null)
  const [disableDependents, setDisableDependents] = useState<SkillDependent[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [allSkills, setAllSkills] = useState<Skill[]>([])
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const skillsQuery = useQuery({
    queryKey: ['skills', 'global'],
    queryFn: () => apiFetch<SkillListResponse>('/api/v1/skills?scope=Global'),
  })

  useEffect(() => {
    if (skillsQuery.data) {
      setAllSkills(skillsQuery.data.items)
      setNextCursor(skillsQuery.data.nextCursor)
    }
  }, [skillsQuery.data])

  const handleLoadMore = async () => {
    if (!nextCursor) return
    setIsLoadingMore(true)
    try {
      const url = `/api/v1/skills?scope=Global&cursor=${encodeURIComponent(nextCursor)}`
      const result = await apiFetch<SkillListResponse>(url)
      setAllSkills(prev => [...prev, ...result.items])
      setNextCursor(result.nextCursor)
    } catch (err) {
      console.error('Failed to load more skills', err)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const skills = allSkills

  const createMutation = useMutation({
    mutationFn: (data: SkillCreate) =>
      apiFetch<Skill>('/api/v1/skills', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (skill) => {
      queryClient.invalidateQueries({ queryKey: ['skills', 'global'] })
      setAllSkills([])
      setNextCursor(null)
      onAdded(skill.id)
      setEditorMode('idle')
      setSelectedSkillId(null)
      setEditorForm(emptyEditorForm)
      setEditorError(null)
      setFieldErrors({})
    },
    onError: (err: unknown) => {
      setEditorError(err instanceof Error ? err.message : t('globalSkillsTab.errors.failedToSave'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SkillUpdate }) =>
      apiFetch<Skill>(`/api/v1/skills/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills', 'global'] })
      setAllSkills([])
      setNextCursor(null)
      setEditorMode('idle')
      setSelectedSkillId(null)
      setEditorError(null)
      setFieldErrors({})
    },
    onError: (err: unknown) => {
      setEditorError(err instanceof Error ? err.message : t('globalSkillsTab.errors.failedToSave'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/api/v1/skills/${id}`, { method: 'DELETE' }),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['skills', 'global'] })
      setAllSkills([])
      setNextCursor(null)
      onRemoved(id)
      if (selectedSkillId === id) { setSelectedSkillId(null); setEditorMode('idle') }
      setDeleteConfirm(null)
      setDeleteConfirmDependents([])
      setDeleteError(null)
    },
    onError: (err: unknown) => {
      setDeleteError(err instanceof Error ? err.message : t('globalSkillsTab.errors.failedToDelete'))
      setDeleteConfirm(null)
      setDeleteConfirmDependents([])
    },
  })

  function handleAdd() {
    setSelectedSkillId(null)
    setEditorMode('create')
    setEditorForm(emptyEditorForm)
    setOriginalForm(emptyEditorForm)
    setEditorError(null)
    setFieldErrors({})
  }

  function handleEdit(skill: Skill) {
    setSelectedSkillId(skill.id)
    setEditorMode('edit')
    const form: EditorForm = { name: skill.name, type: skill.type, shortDescription: skill.shortDescription ?? '', agentSummary: skill.agentSummary ?? '', definition: skill.definition ?? '' }
    setEditorForm(form)
    setOriginalForm(form)
    setEditorError(null)
    setFieldErrors({})
  }

  function closeEditor() {
    setEditorMode('idle')
    setSelectedSkillId(null)
    setEditorForm(emptyEditorForm)
    setOriginalForm(null)
    setShowDiscardDialog(false)
    setEditorError(null)
    setFieldErrors({})
  }

  function handleCancel() {
    if (isEditorDirty) {
      setShowDiscardDialog(true)
    } else {
      closeEditor()
    }
  }

  function requestSwitch(target: Skill | 'create') {
    if (isEditorDirty) {
      setPendingEntitySwitch(target)
      setShowDiscardDialog(true)
    } else if (target === 'create') {
      handleAdd()
    } else {
      handleEdit(target)
    }
  }

  function handleSave() {
    const errorKeys = getSkillFormErrors(editorForm)
    if (Object.keys(errorKeys).length > 0) {
      setFieldErrors(buildSkillFieldErrors(errorKeys, t))
      focusFirstSkillFormError(errorKeys, 'gskill')
      return
    }
    setFieldErrors({})
    setEditorError(null)

    const trimmedName = editorForm.name.trim()
    if (editorMode === 'create') {
      createMutation.mutate({ name: trimmedName, type: editorForm.type, scope: 'Global', definition: editorForm.definition.trim(), shortDescription: editorForm.shortDescription.trim(), agentSummary: editorForm.agentSummary.trim() })
    } else if (editorMode === 'edit' && selectedSkillId) {
      updateMutation.mutate({ id: selectedSkillId, data: { name: trimmedName, type: editorForm.type, definition: editorForm.definition.trim(), shortDescription: editorForm.shortDescription.trim(), agentSummary: editorForm.agentSummary.trim() } })
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  function getBinding(skillId: string) { return skillBindings.find(b => b.skillId === skillId) }
  function getSavedBinding(skillId: string) { return savedBindings.find(b => b.skillId === skillId) }
  function showDependentsHint(skillId: string) {
    const saved = getSavedBinding(skillId)
    const cur = getBinding(skillId)
    return saved?.isEnabled === true && cur?.isEnabled === false
  }

  return (
    <Card title={t('globalSkillsTab.title')} subtitle={t('globalSkillsTab.subtitle')} className="editor-split-card" noPadding>
      <div className="editor-split-body">
        {/* Left pane: skill list */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          {skillsQuery.isLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>}
          {skillsQuery.isError && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{t('globalSkillsTab.errors.failedToSave')}</div>}
          {!skillsQuery.isLoading && skills.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              {t('globalSkillsTab.noSkills')}
            </div>
          )}
          {skills.map(skill => {
            const binding = getBinding(skill.id)
            const isEnabled = binding?.isEnabled ?? true
            const hint = showDependentsHint(skill.id)
            const isSelected = selectedSkillId === skill.id && editorMode === 'edit'
            return (
              <div key={skill.id}>
                <div
                  className={`skill-row${isSelected ? ' selected' : ''}`}
                  onClick={() => requestSwitch(skill)}
                >
                  <div className="conn-icon icon-skill" aria-hidden="true">
                    <LightningIcon />
                  </div>
                  <div className="skill-info" style={{ opacity: isEnabled ? 1 : 0.55 }}>
                    <div className="skill-name">{skill.name}</div>
                    {skill.shortDescription && <div className="skill-desc">{skill.shortDescription}</div>}
                  </div>
                  <TypeBadge type={skill.type} />
                  <Toggle
                    checked={isEnabled}
                    onChange={(v) => {
                      if (!v) {
                        setDisableConfirm(skill)
                        apiFetch<SkillDependentsResponse>(`/api/v1/skills/${skill.id}/dependents`)
                          .then((r) => setDisableDependents(r.items))
                          .catch(() => setDisableDependents([]))
                      } else {
                        onToggle(skill.id, true)
                      }
                    }}
                    label={t('globalSkillsTab.toggleAria', { name: skill.name })}
                  />
                  <button
                    type="button"
                    className="skill-edit-btn"
                    onClick={(e) => { e.stopPropagation(); requestSwitch(skill) }}
                    aria-label={t('globalSkillsTab.editAria', { name: skill.name })}
                  >
                    <PencilIcon />
                  </button>
                  <button
                    type="button"
                    className="icon-btn danger"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeleteConfirm(skill)
                      setDeleteConfirmDependents([])
                      apiFetch<SkillDependentsResponse>(`/api/v1/skills/${skill.id}/dependents`)
                        .then((r) => setDeleteConfirmDependents(r.items))
                        .catch(() => setDeleteConfirmDependents([]))
                    }}
                    aria-label={t('globalSkillsTab.deleteAria', { name: skill.name })}
                  >
                    <TrashIcon />
                  </button>
                </div>
                {hint && (
                  <div data-testid={`skill-deps-hint-${skill.id}`} style={{ fontSize: 12, color: 'var(--warning)', padding: '4px 0 4px 46px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <WarningIcon />
                    {t('globalSkillsTab.disableWarning')}
                  </div>
                )}
              </div>
            )
          })}
          {deleteError && (
            <div role="alert" style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)' }}>{deleteError}</div>
          )}
          {nextCursor && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="btn btn-secondary"
                style={{ width: '100%', fontSize: 12, opacity: isLoadingMore ? 0.6 : 1, cursor: isLoadingMore ? 'default' : 'pointer' }}
              >
                {isLoadingMore ? <Spinner size={12} /> : t('globalSkillsTab.showMore')}
              </button>
            </div>
          )}
        </div>

        {/* Separator */}
        <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

        {/* Right pane: editor */}
        <div className="editor-pane-col">
          {editorMode === 'idle' && (
            <div className="editor-pane-empty">
              <LightningIcon />
              <p style={{ fontSize: 13 }}>{t('globalSkillsTab.idle')}</p>
              <button type="button" onClick={() => requestSwitch('create')} className="btn btn-primary">{t('globalSkillsTab.addNew')}</button>
            </div>
          )}
          {(editorMode === 'create' || editorMode === 'edit') && (
            <div className="editor-form-wrap">
              <div className="editor-form-scroll">
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
                  {editorMode === 'create' ? t('globalSkillsTab.addNew') : t('globalSkillsTab.editTitle')}
                </div>
                {editorError && <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{editorError}</div>}
                <SkillForm
                  idPrefix="gskill"
                  value={editorForm}
                  errors={fieldErrors}
                  onChange={(patch) => {
                    setEditorForm(f => ({ ...f, ...patch }))
                    setFieldErrors(prev => {
                      const next = { ...prev }
                      for (const key of Object.keys(patch) as (keyof SkillFieldErrors)[]) delete next[key]
                      return next
                    })
                  }}
                  namePlaceholder={t('globalSkillsTab.form.namePlaceholder')}
                />
              </div>
              <div className="editor-form-bar">
                <button type="button" onClick={handleCancel} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>{t('globalSkillsTab.form.cancel')}</button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || Object.keys(fieldErrors).length > 0}
                  className="btn btn-primary"
                  style={{ fontSize: 12, padding: '5px 12px', opacity: (isSaving || Object.keys(fieldErrors).length > 0) ? 0.65 : 1, cursor: (isSaving || Object.keys(fieldErrors).length > 0) ? 'default' : 'pointer' }}
                >
                  {isSaving ? <Spinner size={12} /> : null}
                  {t('globalSkillsTab.form.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {deleteConfirm && (
        <ConfirmDialog
          title={t('globalSkillsTab.deleteDialog.title', { name: deleteConfirm.name })}
          message={t('globalSkillsTab.deleteDialog.message')}
          body={deleteConfirmDependents.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                {t('globalSkillsTab.deleteDialog.affectedAgentsLabel')}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {deleteConfirmDependents.map((d) => (
                  <li key={d.agentId}>{d.agentDisplayName}</li>
                ))}
              </ul>
            </div>
          ) : undefined}
          confirmLabel={t('globalSkillsTab.deleteDialog.confirm')}
          danger
          requiresAcknowledgment={deleteConfirmDependents.length > 0}
          acknowledgmentLabel={t('globalSkillsTab.deleteDialog.acknowledgment')}
          onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
          onCancel={() => { setDeleteConfirm(null); setDeleteConfirmDependents([]) }}
        />
      )}
      {disableConfirm && (
        <ConfirmDialog
          title={t('globalSkillsTab.disableDialog.title', { name: disableConfirm.name })}
          message={t('globalSkillsTab.disableDialog.message')}
          body={disableDependents.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                {t('globalSkillsTab.disableDialog.affectedAgentsLabel')}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {disableDependents.map((d) => (
                  <li key={d.agentId}>{d.agentDisplayName}</li>
                ))}
              </ul>
            </div>
          ) : undefined}
          confirmLabel={t('globalSkillsTab.disableDialog.confirm')}
          danger
          onConfirm={() => { onToggle(disableConfirm.id, false); setDisableConfirm(null); setDisableDependents([]) }}
          onCancel={() => { setDisableConfirm(null); setDisableDependents([]) }}
        />
      )}
      {showDiscardDialog && (
        <UnsavedChangesDialog
          onConfirm={() => {
            setShowDiscardDialog(false)
            if (pendingEntitySwitch === 'create') {
              handleAdd()
            } else if (pendingEntitySwitch) {
              handleEdit(pendingEntitySwitch)
            } else {
              closeEditor()
            }
            setPendingEntitySwitch(null)
          }}
          onCancel={() => {
            setShowDiscardDialog(false)
            setPendingEntitySwitch(null)
          }}
        />
      )}
    </Card>
  )
}
